import React from 'react';
import { useTranslation } from 'react-i18next';

import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import {
  AGENT_UNAVAILABLE_TERMINAL,
  DRY_RUN_AGENT_UNAVAILABLE_TERMINAL,
} from '@/common/oralExam/faultPresentation';
import { isSelfServiceRun } from '@/common/utils/oralExam/runConduct';

import { OralExamErrorState } from './OralExamErrorState';

type AgentUnavailableTerminalScreenProps = {
  runState: OralExamRunStateResponse;
  leave: () => void;
  leaveLabel?: string;
  onStartDryRun?: () => void | Promise<void>;
  isStartingDryRun?: boolean;
  startDryRunError?: string | null;
  testID?: string;
};

function isOneToNExam(runState: OralExamRunStateResponse): boolean {
  return (runState.capacity ?? 1) > 1;
}

/**
 * Terminal screen when an examiner-agent exam ends as agent_unavailable.
 */
export function AgentUnavailableTerminalScreen({
  runState,
  leave,
  leaveLabel,
  onStartDryRun,
  isStartingDryRun = false,
  startDryRunError = null,
  testID = 'oral-exam-agent-unavailable-dead',
}: AgentUnavailableTerminalScreenProps) {
  const { t } = useTranslation();
  const dryRun = isSelfServiceRun(runState);

  if (dryRun) {
    return (
      <OralExamErrorState
        testID="oral-exam-dry-run-agent-unavailable"
        title={t(DRY_RUN_AGENT_UNAVAILABLE_TERMINAL.title.copyKey, {
          defaultValue: DRY_RUN_AGENT_UNAVAILABLE_TERMINAL.title.defaultValue,
        })}
        message={t(DRY_RUN_AGENT_UNAVAILABLE_TERMINAL.body.copyKey, {
          defaultValue: DRY_RUN_AGENT_UNAVAILABLE_TERMINAL.body.defaultValue,
        })}
        tone="warning"
        retry={onStartDryRun}
        retryLabel={t(DRY_RUN_AGENT_UNAVAILABLE_TERMINAL.retry.copyKey, {
          defaultValue: DRY_RUN_AGENT_UNAVAILABLE_TERMINAL.retry.defaultValue,
        })}
        retryTestID="oral-exam-dry-run-start-again"
        actionError={startDryRunError}
        isRetryPending={isStartingDryRun}
        leave={leave}
        leaveLabel={leaveLabel ?? t('oralExam.leaveExam', { defaultValue: 'Leave exam' })}
      />
    );
  }

  if (!isOneToNExam(runState)) {
    return null;
  }

  return (
    <OralExamErrorState
      testID={testID}
      title={t(AGENT_UNAVAILABLE_TERMINAL.title.copyKey, {
        defaultValue: AGENT_UNAVAILABLE_TERMINAL.title.defaultValue,
      })}
      message={t(AGENT_UNAVAILABLE_TERMINAL.body.copyKey, {
        defaultValue: AGENT_UNAVAILABLE_TERMINAL.body.defaultValue,
      })}
      tone="warning"
      leave={leave}
      leaveLabel={leaveLabel ?? t('oralExam.leaveExam', { defaultValue: 'Leave exam' })}
    />
  );
}

export function shouldShowAgentUnavailableTerminal(runState: OralExamRunStateResponse): boolean {
  if (runState.state !== 'aborted' || runState.endReason !== 'agent_unavailable') {
    return false;
  }
  return isSelfServiceRun(runState) || isOneToNExam(runState);
}
